import { defineStore } from "pinia";
import _ from "lodash";
import type { TableSetting } from "~/interface/TableState";
import { computed, ref, watch } from "vue";
import { usePosTableInstanceStore } from "./pos.tableInstance";
import type { TableBilling } from "~/interface/TableBilling";
import { useStorage } from "@vueuse/core";
import dayjs from "dayjs";
import { setDoc, onSnapshot } from "firebase/firestore";
import { child, onChildAdded, onChildChanged, onChildRemoved, onValue, set } from "firebase/database";
import { bucket, isOfflineBoot, offlineBucketNames } from "~/services/storage/offlineBuckets";
import { loadSingleRow, waitForFirstRTDBSettle, isMainCashierRuntime } from "~/services/bootstrap/layerBHelpers";

function getTableStatesBucket() {
  const sid = useAuthStore().storeId;
  if (!sid) return null;
  return bucket(offlineBucketNames.tableStates(String(sid)));
}

export const usePosTableStatesStore = defineStore("table-state", () => {
  const loaded = ref(false)
  const tablesState = ref<{ [table: string]: TableSetting }>({})
  const tableBillingGlobal = ref<TableBilling>({
    sc: [0, 0],
    vat: [0, 0],
    roundingMode: "5/4",
  });

  const nonce = ref(dayjs().valueOf())


  const fetchSnapshots = () => {
     
    const tableStateRef = useRealtimeDatabase().tablesStateRef()
    

    onChildAdded(tableStateRef, (data) => {
      tablesState.value[data.key] = data.val() || {}
    });

    onChildChanged(tableStateRef, (snapshot) => {
      tablesState.value[snapshot.key] = snapshot.val() || {}
    })

    onChildRemoved(tableStateRef, (data) => {
      tablesState.value[data.key]  = null
    });
    loaded.value = true


  }

  if (process.client) {
    // @ts-ignore
    window.TABLE_STATE = tablesState;
  }

  // Mirror tablesState changes (both inbound RTDB events and outbound local
  // mutations) into IDB. Debounced so a burst of child events coalesces.
  const persistToIDB = _.debounce(() => {
    // Only the Main Cashier owns the runtime IDB mirror — skip on
    // terminal / 2nd cashier / readonly.
    if (!isMainCashierRuntime()) return
    const b = getTableStatesBucket()
    if (!b) return
    void b.set("state", tablesState.value)
  }, 300)

  async function bootOnline() {
    fetchSnapshots()
    watch(tablesState, () => persistToIDB(), { deep: true })
    await waitForFirstRTDBSettle(useRealtimeDatabase().tablesStateRef())
    loaded.value = true
  }

  async function bootOffline() {
    const cached = await loadSingleRow<{ [table: string]: TableSetting }>(
      getTableStatesBucket(),
      "state"
    )
    if (cached) tablesState.value = cached
    loaded.value = true
    fetchSnapshots()
    watch(tablesState, () => persistToIDB(), { deep: true })
  }

  async function init() {
    if (isOfflineBoot()) return bootOffline()
    return bootOnline()
  }

  const getTableState = (tableName: string) => {
    return tablesState.value[tableName];
  };

  const isLockedWithTable = (tableName: string) => {
    return tablesState.value[tableName]?.lock || false;
  };


  const setLockTable = (tableName: string, forceLock?: boolean) => {
    const tableStateRef = useRealtimeDatabase().tablesStateRef()
    const _dbRef = child(tableStateRef, `${tableName}/lock`)
    if (typeof forceLock === 'boolean') {
      set(_dbRef, forceLock)
    } else {
      const current = tablesState.value[tableName]?.lock || false
      set(_dbRef, !current)
    }
  };

  const setPrintTime = (tableName: string, printTime: any) => {
    const tableStateRef = useRealtimeDatabase().tablesStateRef()
    const _dbRef = child(tableStateRef, `${tableName}/printTime`)
    console.log("_dbRef=>", _dbRef)
    console.log("printTime=>", printTime)
    set(_dbRef, printTime)
  };

  const setTableBillingSetting = (tableName: string, settings: Partial<TableSetting>) => {
    const tableStateRef = useRealtimeDatabase().tablesStateRef()
    Object.entries(settings).forEach(([key, val]) => {
      const _dbRef = child(tableStateRef, `${tableName}/${key}`)
      set(_dbRef, val)
    })
  };


  const clearTableState = (table: string, payload: any = null) => {
    const tableStateRef = useRealtimeDatabase().tablesStateRef()
    const _dbRef = child(tableStateRef, table)
    set(_dbRef, null)
  };

  const clearAllTablesState = () => {
    const tableStateRef = useRealtimeDatabase().tablesStateRef()
    set(tableStateRef, null)
  };

  return {
    name: "tableStates",
    init,
    bootOnline,
    bootOffline,
    loaded,
    getTableState,
    isLockedWithTable,
    nonce,
    setLockTable,
    setPrintTime,
    setTableBillingSetting,
    tablesState,
    tableBillingGlobal,
    clearTableState,
    clearAllTablesState
  };
});


